// src/utils/pdfExport.js
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Exporte les résultats d'optimisation au format PDF
 * @param {Object} resultat - Résultat de l'optimisation
 * @param {Array} murs - Liste des murs du projet
 * @param {Object} dimensionsPlaque - Dimensions des plaques standard
 */
export const exporterResultatsEnPDF = async (resultat, murs, dimensionsPlaque) => {
  // Créer un nouveau document PDF au format A4
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;

  // Fonction pour ajouter une nouvelle page
  const ajouterPage = () => {
    pdf.addPage();
    pdf.setFontSize(10);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Optimisation des découpes de placoplatre - Page ' + pdf.getNumberOfPages(), pageWidth / 2, pageHeight - 5, { align: 'center' });
    y = margin;
  };

  // Position verticale courante
  let y = margin;

  // Ajout du titre et infos du projet
  pdf.setFontSize(22);
  pdf.setTextColor(58, 134, 255); // Couleur primaire
  pdf.text('Optimisation des découpes de placoplatre', pageWidth / 2, y, { align: 'center' });
  y += 12;

  pdf.setFontSize(12);
  pdf.setTextColor(80, 80, 80);
  const dateStr = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  pdf.text(`Projet généré le ${dateStr}`, pageWidth / 2, y, { align: 'center' });
  y += 15;

  // Informations sur les plaques
  pdf.setFontSize(14);
  pdf.setTextColor(58, 134, 255);
  pdf.text('Informations générales', margin, y);
  y += 8;

  pdf.setFontSize(11);
  pdf.setTextColor(80, 80, 80);
  pdf.text(`Dimensions des plaques standard : ${dimensionsPlaque.largeur} × ${dimensionsPlaque.hauteur} cm`, margin, y);
  y += 6;

  pdf.text(`Nombre total de murs : ${murs.length}`, margin, y);
  y += 6;

  pdf.text(`Nombre total de plaques nécessaires : ${resultat.nbPlaques}`, margin, y);
  y += 6;

  pdf.text(`Surface totale des plaques : ${(resultat.surfaceTotale / 10000).toFixed(2)} m²`, margin, y);
  y += 6;

  pdf.text(`Surface utile : ${(resultat.surfaceUtile / 10000).toFixed(2)} m²`, margin, y);
  y += 6;

  pdf.text(`Pourcentage de chutes : ${resultat.pourcentageChutes.toFixed(2)}%`, margin, y);
  y += 15;

  // Détails des murs
  pdf.setFontSize(14);
  pdf.setTextColor(58, 134, 255);
  pdf.text('Détails par mur', margin, y);
  y += 10;

  // Parcourir chaque mur
  for (let i = 0; i < murs.length; i++) {
    const mur = murs[i];

    // Vérifier s'il y a assez d'espace sur la page
    if (y > pageHeight - 50) {
      ajouterPage();
    }

    // Cadre pour le mur
    pdf.setDrawColor(200, 200, 200);
    pdf.setFillColor(248, 248, 248);
    pdf.roundedRect(margin, y - 5, pageWidth - 2 * margin, 35, 2, 2, 'F');

    pdf.setFontSize(13);
    pdf.setTextColor(58, 134, 255);
    pdf.text(`Mur ${i + 1} : ${mur.nom}`, margin + 5, y + 5);

    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Dimensions : ${mur.largeur} × ${mur.hauteur} cm`, margin + 5, y + 12);

    // Calculer la surface du mur et des ouvertures
    const surfaceMur = mur.largeur * mur.hauteur;
    const surfaceOuvertures = mur.ouvertures.reduce((acc, o) => acc + (o.largeur * o.hauteur), 0);
    const surfaceUtileMur = surfaceMur - surfaceOuvertures;

    pdf.text(`Surface utile : ${(surfaceUtileMur / 10000).toFixed(2)} m²`, margin + 5, y + 18);

    // Nombre de plaques pour ce mur
    const plaquesParMur = resultat.plaques.filter(p => p.murId === mur.id);
    pdf.text(`Nombre de plaques : ${plaquesParMur.length}`, margin + 5, y + 24);

    // Estimation Ossature
    const ossature = resultat.murDetails?.find(d => d.murId === mur.id)?.ossature?.stats;
    if (ossature) {
      pdf.text(`Ossature estimée : ${ossature.montants} montants | ${(ossature.longueurTotaleRails / 100).toFixed(1)}m rails`, margin + 5, y + 30);
    }

    // Informations sur les ouvertures
    if (mur.ouvertures.length > 0) {
      pdf.text(`Ouvertures : ${mur.ouvertures.length}`, pageWidth - margin - 60, y + 12);

      for (let j = 0; j < Math.min(mur.ouvertures.length, 3); j++) {
        const ouv = mur.ouvertures[j];
        pdf.text(`- ${ouv.type} : ${ouv.largeur}×${ouv.hauteur}cm (${ouv.x}, ${ouv.y})`, pageWidth - margin - 60, y + 18 + j * 6);
      }

      if (mur.ouvertures.length > 3) {
        pdf.text(`- ... et ${mur.ouvertures.length - 3} autres ouvertures`, pageWidth - margin - 60, y + 18 + 3 * 6);
      }
    } else {
      pdf.text(`Ouvertures : aucune`, pageWidth - margin - 60, y + 12);
    }

    y += 40;

    // Vérifier s'il y a assez d'espace pour les détails des plaques
    if (y > pageHeight - 80) {
      ajouterPage();
    }

    // Détails des plaques pour ce mur
    pdf.setFontSize(11);
    pdf.setTextColor(58, 134, 255);
    pdf.text(`Détails des plaques pour ${mur.nom}`, margin, y);
    y += 8;

    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);

    // En-têtes du tableau
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, y - 4, pageWidth - 2 * margin, 8, 'F');
    pdf.text('N°', margin + 5, y);
    pdf.text('Position', margin + 20, y);
    pdf.text('Dimensions', margin + 65, y);
    pdf.text('Orientation', margin + 120, y);
    pdf.text('Ajustement', margin + 160, y);
    pdf.text('Découpes', pageWidth - margin - 30, y);
    y += 4;

    // Lignes du tableau
    for (let j = 0; j < plaquesParMur.length; j++) {
      const plaque = plaquesParMur[j];

      // Vérifier s'il y a assez d'espace pour cette ligne
      if (y > pageHeight - 20) {
        ajouterPage();

        // Répéter les en-têtes
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, y - 4, pageWidth - 2 * margin, 8, 'F');
        pdf.text('N°', margin + 5, y);
        pdf.text('Position', margin + 20, y);
        pdf.text('Dimensions', margin + 65, y);
        pdf.text('Orientation', margin + 120, y);
        pdf.text('Ajustement', margin + 160, y);
        pdf.text('Découpes', pageWidth - margin - 30, y);
        y += 4;
      }

      // Alterner les couleurs de fond
      if (j % 2 === 0) {
        pdf.setFillColor(248, 248, 248);
        pdf.rect(margin, y, pageWidth - 2 * margin, 8, 'F');
      }

      pdf.text(`${j + 1}`, margin + 5, y + 4);
      pdf.text(`(${plaque.x}, ${plaque.y}) cm`, margin + 20, y + 4);
      pdf.text(`${plaque.largeur} × ${plaque.hauteur} cm`, margin + 65, y + 4);
      pdf.text(`${plaque.orientation === 'normal' ? 'Horizontale' : 'Verticale'}`, margin + 120, y + 4);
      pdf.text(`${plaque.ajustementNecessaire ? 'Requis' : 'Aucun'}`, margin + 160, y + 4);
      pdf.text(`${plaque.decoupes.length}`, pageWidth - margin - 30, y + 4);

      y += 8;

      // Si la plaque a des découpes, les lister
      if (plaque.decoupes.length > 0) {
        for (let k = 0; k < plaque.decoupes.length; k++) {
          const decoupe = plaque.decoupes[k];

          // Vérifier s'il y a assez d'espace pour cette ligne
          if (y > pageHeight - 15) {
            ajouterPage();
          }

          pdf.setFontSize(8);
          pdf.setTextColor(120, 120, 120);
          const xLocal = decoupe.xLocal || (decoupe.x - plaque.x);
          const yLocal = decoupe.yLocal || (decoupe.y - plaque.y);
          pdf.text(`   - Découpe ${k + 1} : ${decoupe.type || 'autre'} - Position: (${xLocal}, ${yLocal}) cm - Dimensions: ${decoupe.largeur} × ${decoupe.hauteur} cm`, margin + 20, y + 3);

          pdf.setFontSize(9);
          pdf.setTextColor(80, 80, 80);
          y += 7;
        }
        y += 2;
      }
    }

    y += 10;
  }

  // Ajouter des instructions ou notes
  if (y > pageHeight - 60) {
    ajouterPage();
  }

  pdf.setFontSize(12);
  pdf.setTextColor(58, 134, 255);
  pdf.text('Notes pour l\'installation', margin, y);
  y += 8;

  pdf.setFontSize(10);
  pdf.setTextColor(80, 80, 80);

  const notes = [
    "• Commencez par vérifier les dimensions des murs et l'emplacement des ouvertures.",
    "• Pour chaque mur, respectez l'ordre de pose des plaques indiqué.",
    "• Marquez les positions des découpes avec précision avant de les réaliser.",
    "• Les plaques nécessitant des ajustements doivent être découpées aux dimensions indiquées.",
    "• Pour les ouvertures complexes, découpez d'abord les grandes sections puis affinez.",
    "• Conservez les chutes importantes, elles pourront être utilisées pour d'autres projets."
  ];

  for (let i = 0; i < notes.length; i++) {
    pdf.text(notes[i], margin, y);
    y += 6;
  }

  // Section Plans de coupe détaillés
  pdf.setFontSize(14);
  pdf.setTextColor(58, 134, 255);
  // On ne met pas de titre de section ici car chaque plan est sur une nouvelle page

  // Fonction pour dessiner une flèche de dimension
  const drawDimension = (doc, x1, y1, x2, y2, text, vertical = false, offset = 5) => {
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.2);
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);

    // Lignes de rappel
    if (vertical) {
      doc.line(x1 - offset, y1, x1, y1);
      doc.line(x2 - offset, y2, x2, y2);
      doc.line(x1 - offset + 2, y1, x1 - offset + 2, y2); // Ligne de cote

      // Flèches
      // doc.triangle(...) - Simplifié par des traits pour l'instant

      // Texte (tourné)
      doc.text(text, x1 - offset, y1 + (y2 - y1) / 2, { angle: 90, align: 'center' });
    } else {
      doc.line(x1, y1 + offset, x1, y1);
      doc.line(x2, y2 + offset, x2, y2);
      doc.line(x1, y1 + offset - 2, x2, y2 + offset - 2); // Ligne de cote

      doc.text(text, x1 + (x2 - x1) / 2, y1 + offset + 2, { align: 'center' });
    }
  };

  // Parcourir tous les murs et toutes les plaques pour générer les plans de coupe
  for (let i = 0; i < murs.length; i++) {
    const mur = murs[i];
    const plaquesParMur = resultat.plaques.filter(p => p.murId === mur.id);

    // Trier les plaques par index pour l'ordre logique
    // (supposons qu'elles sont déjà triées ou qu'on peut utiliser leur index dans la liste)

    for (let j = 0; j < plaquesParMur.length; j++) {
      const plaque = plaquesParMur[j];

      // Générer une page de détail uniquement si la plaque nécessite une découpe ou un ajustement
      if (plaque.ajustementNecessaire || (plaque.decoupes && plaque.decoupes.length > 0)) {
        ajouterPage();

        // Titre de la page
        pdf.setFontSize(16);
        pdf.setTextColor(58, 134, 255);
        pdf.text(`Plan de découpe - Mur : ${mur.nom} - Plaque #${j + 1}`, margin, margin + 10);

        y = margin + 25;

        // --- Informations textuelles ---
        pdf.setFontSize(10);
        pdf.setTextColor(80, 80, 80);

        // Colonne gauche
        pdf.text(`Dimensions finales : ${plaque.largeur} × ${plaque.hauteur} cm`, margin, y);
        y += 6;
        pdf.text(`Position sur mur : x=${plaque.x}, y=${plaque.y} cm`, margin, y);
        y += 6;

        const orientationStr = plaque.orientation === 'normal' ? 'Horizontale' : 'Verticale';
        const dimOrigine = plaque.orientation === 'normal'
          ? `${dimensionsPlaque.largeur} × ${dimensionsPlaque.hauteur}`
          : `${dimensionsPlaque.hauteur} × ${dimensionsPlaque.largeur}`;

        pdf.text(`Plaque d'origine : ${dimOrigine} cm (${orientationStr})`, margin, y);

        // Colonne droite (Types de découpes)
        y -= 12;
        const col2X = pageWidth / 2 + 10;

        if (plaque.ajustementNecessaire) {
          pdf.setTextColor(230, 57, 70); // Rouge
          pdf.text("(!) Ajustement de taille nécessaire", col2X, y);
          pdf.setTextColor(80, 80, 80);
          y += 6;
        }

        if (plaque.decoupes && plaque.decoupes.length > 0) {
          pdf.text(`Nombre de découpes internes : ${plaque.decoupes.length}`, col2X, y);
        }

        y += 20;

        // --- Schéma visuel ---
        const espaceDessinHauteur = 120;
        const espaceDessinLargeur = pageWidth - 2 * margin;

        // Cadre de la zone de dessin
        pdf.setDrawColor(240, 240, 240);
        pdf.setFillColor(252, 252, 252);
        pdf.rect(margin, y, espaceDessinLargeur, espaceDessinHauteur, 'F');

        // Calcul de l'échelle pour faire tenir la plaque d'origine dans la zone
        const plaqueOrigineLargeur = plaque.orientation === 'normal' ? dimensionsPlaque.largeur : dimensionsPlaque.hauteur;
        const plaqueOrigineHauteur = plaque.orientation === 'normal' ? dimensionsPlaque.hauteur : dimensionsPlaque.largeur;

        // Marge interne dans le dessin (mm)
        const padding = 20;

        const ratioX = (espaceDessinLargeur - 2 * padding) / plaqueOrigineLargeur;
        const ratioY = (espaceDessinHauteur - 2 * padding) / plaqueOrigineHauteur;
        const scale = Math.min(ratioX, ratioY);

        // Origine du dessin (coin bas gauche visuel, mais attention aux coordonnées PDF qui sont top-left)
        // On veut centrer le dessin
        const dessinLargeur = plaqueOrigineLargeur * scale;
        const dessinHauteur = plaqueOrigineHauteur * scale;

        const originX = margin + (espaceDessinLargeur - dessinLargeur) / 2;
        const originY = y + (espaceDessinHauteur - dessinHauteur) / 2 + dessinHauteur; // Bas du dessin en Y PDF

        // Fonction helpers pour convertir coords (cm) -> PDF (mm)
        // Y inversé car PDF st y=0 en haut, et nos coords sont y=0 en bas
        const toPdfX = (valCM) => originX + valCM * scale;
        const toPdfY = (valCM) => originY - valCM * scale;
        const toPdfW = (valCM) => valCM * scale;
        const toPdfH = (valCM) => valCM * scale;

        // 1. Dessiner la plaque d'origine (Pointillés)
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.3);
        pdf.setLineDash([3, 3], 0);
        pdf.rect(toPdfX(0), toPdfY(plaqueOrigineHauteur), toPdfW(plaqueOrigineLargeur), toPdfH(plaqueOrigineHauteur));
        pdf.setLineDash([]); // Reset

        // Légende plaque origine
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text("Plaque standard", toPdfX(plaqueOrigineLargeur / 2), toPdfY(plaqueOrigineHauteur) - 2, { align: 'center' });

        // 2. Dessiner la plaque finale (Trait plein bleu)
        pdf.setDrawColor(58, 134, 255);
        pdf.setLineWidth(0.5);
        pdf.setFillColor(233, 240, 255); // Bleu très très clair
        pdf.rect(toPdfX(0), toPdfY(plaque.hauteur), toPdfW(plaque.largeur), toPdfH(plaque.hauteur), 'FD');

        // Dimensions Plaque Finale
        pdf.setTextColor(58, 134, 255);
        // Largeur
        pdf.text(`${plaque.largeur} cm`, toPdfX(plaque.largeur / 2), toPdfY(0) + 5, { align: 'center' });
        // Hauteur
        pdf.text(`${plaque.hauteur} cm`, toPdfX(0) - 5, toPdfY(plaque.hauteur / 2), { align: 'right' });

        // 3. Dessiner les découpes internes (Rouge)
        if (plaque.decoupes) {
          plaque.decoupes.forEach((decoupe, idx) => {
            const xLoc = decoupe.xLocal || (decoupe.x - plaque.x);
            const yLoc = decoupe.yLocal || (decoupe.y - plaque.y);

            pdf.setDrawColor(230, 57, 70);
            pdf.setFillColor(255, 240, 240);
            pdf.setLineWidth(0.3);
            pdf.rect(toPdfX(xLoc), toPdfY(yLoc + decoupe.hauteur), toPdfW(decoupe.largeur), toPdfH(decoupe.hauteur), 'FD');

            // Centre de la découpe pour le numéro
            const cx = toPdfX(xLoc + decoupe.largeur / 2);
            const cy = toPdfY(yLoc + decoupe.hauteur / 2);

            pdf.setTextColor(230, 57, 70);
            pdf.setFontSize(7);
            pdf.text(`${idx + 1}`, cx, cy, { align: 'center', baseline: 'middle' });

            // Cotations simplifiées pour la découpe (Position bas-gauche et taille)
            // Position X
            pdf.text(`${xLoc}`, toPdfX(xLoc), toPdfY(0) - 2, { align: 'center', fontSize: 6 });
            // Position Y (si assez de place)
            if (xLoc > 5) {
              pdf.text(`${yLoc}`, toPdfX(0) + 2, toPdfY(yLoc), { align: 'left', fontSize: 6 });
            }
          });
        }

        y += espaceDessinHauteur + 10;

        // --- Instructions étape par étape ---
        pdf.setFontSize(12);
        pdf.setTextColor(58, 134, 255);
        pdf.text('Instructions de découpe', margin, y);
        y += 8;

        pdf.setFontSize(10);
        pdf.setTextColor(80, 80, 80);

        let step = 1;

        // Etape 1: Mise à la taille (si besoin)
        if (plaque.ajustementNecessaire) {
          pdf.text(`${step}. Découpez la plaque standard aux dimensions ${plaque.largeur} × ${plaque.hauteur} cm.`, margin, y);
          pdf.setFontSize(9);
          pdf.setTextColor(120, 120, 120);
          pdf.text(`   (Coupez ${(plaqueOrigineLargeur - plaque.largeur).toFixed(1)} cm sur la largeur et ${(plaqueOrigineHauteur - plaque.hauteur).toFixed(1)} cm sur la hauteur)`, margin, y + 5);
          pdf.setTextColor(80, 80, 80);
          pdf.setFontSize(10);
          y += 12;
          step++;
        }

        // Etape 2+: Découpes internes
        if (plaque.decoupes && plaque.decoupes.length > 0) {
          pdf.text(`${step}. Tracez et réalisez les découpes internes :`, margin, y);
          y += 6;

          plaque.decoupes.forEach((decoupe, idx) => {
            const xLoc = decoupe.xLocal || (decoupe.x - plaque.x);
            const yLoc = decoupe.yLocal || (decoupe.y - plaque.y);

            // Vérifier saut de page
            if (y > pageHeight - 20) {
              ajouterPage();
              y = margin;
            }

            const typeInfos = decoupe.type ? `Type: ${decoupe.type}` : '';
            pdf.text(`   - Découpe ${idx + 1} (${typeInfos}) :`, margin, y);
            y += 5;
            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`     Position du coin bas-gauche : ${xLoc} cm (horizontal) / ${yLoc} cm (vertical)`, margin, y);
            y += 5;
            pdf.text(`     Taille à découper : largeur ${decoupe.largeur} cm × hauteur ${decoupe.hauteur} cm`, margin, y);
            y += 6;
            pdf.setFontSize(10);
            pdf.setTextColor(80, 80, 80);
          });
          step++;
        }

        // Note finale
        pdf.text(`${step}. Installez la plaque sur le mur ${mur.nom} à la position (x=${plaque.x}, y=${plaque.y}).`, margin, y);
      }
    }
  }

  // Capturer la zone de visualisation pour chaque mur
  try {
    if (document.querySelector('.mur-canvas') && window.onSelectMur) {
      // Sauvegarder le mur actuellement sélectionné pour le restaurer après
      const murOriginal = murs.find(m => m.id === window.murSelectionneeId) || murs[0];
      
      // Pour chaque mur, ajouter une vue en image
      for (let i = 0; i < murs.length; i++) {
        const mur = murs[i];
        
        // Sélectionner le mur courant
        window.onSelectMur(mur.id);
        
        // Laisser le temps au rendu React de se mettre à jour (augmenté à 800ms)
        await new Promise(resolve => setTimeout(resolve, 800));

        ajouterPage();

        pdf.setFontSize(14);
        pdf.setTextColor(58, 134, 255);
        pdf.text(`Visualisation du mur : ${mur.nom}`, pageWidth / 2, y, { align: 'center' });
        y += 10;

        // Attendre que le canvas soit mis à jour avec les données du bon mur
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const canvas = document.querySelector('.mur-canvas');
        if (canvas) {
          const canvasImage = await html2canvas(canvas, {
            scale: 2, // Meilleure qualité
            useCORS: true,
            allowTaint: true
          });
          const imgData = canvasImage.toDataURL('image/png');

          // Calculer les dimensions pour l'image
          const canvasWidth = canvas.offsetWidth;
          const canvasHeight = canvas.offsetHeight;
          const ratio = canvasWidth / canvasHeight;

          const maxWidth = pageWidth - 2 * margin;
          const maxHeight = pageHeight - y - margin;

          let imgWidth = maxWidth;
          let imgHeight = imgWidth / ratio;

          if (imgHeight > maxHeight) {
            imgHeight = maxHeight;
            imgWidth = imgHeight * ratio;
          }

          pdf.addImage(imgData, 'PNG', margin + (maxWidth - imgWidth) / 2, y, imgWidth, imgHeight);
          y += imgHeight + 10;
        }
      }
      
      // Restaurer le mur original
      if (murOriginal) {
        window.onSelectMur(murOriginal.id);
      }
    }
  } catch (error) {
    console.error("Erreur lors de la capture des visualisations :", error);
    // Continuer sans les images si une erreur se produit
  }

  // Pied de page
  pdf.setFontSize(10);
  pdf.setTextColor(150, 150, 150);
  pdf.text('Optimisation des découpes de placoplatre - Page ' + pdf.getNumberOfPages(), pageWidth / 2, pageHeight - 5, { align: 'center' });

  // Sauvegarder le PDF
  const nomFichier = `Optimisation_Placoplatre_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(nomFichier);

  return nomFichier;
};